/**
 * Top-level widget shell. Composes:
 *  - FabButton (toggle)
 *  - Dialog → Panel
 *  - Listens to EVENT_NAMES.OPEN/CLOSE so window.BlakfyA11y.open() works
 *  - Optional Alt+0 keyboard shortcut
 */
import { useEffect, useId, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import {
  EVENT_NAMES,
  emit,
  getPreferences,
  on,
  type IconStyle,
  type Theme,
  type Translation,
  type WidgetOptions,
} from '@blakfy/a11y-core';
import { FabButton } from './FabButton';
import { Dialog } from './Dialog';
import { Panel } from './Panel';
import { FloatingBadge } from './FloatingBadge';
import { ReadingMask } from './ReadingMask';
import { Magnifier } from './Magnifier';

type Props = {
  config: WidgetOptions;
  translation: Translation;
  iconStyle?: IconStyle;
  keyboardShortcut?: boolean;
  onThemeChange?: (theme: Theme) => void;
  /** #50: real timeToFirstClick tracking — fires on the FAB's first real click only. */
  onFirstFabClick?: () => void;
};

export function Widget({
  config,
  translation,
  iconStyle = 'access',
  keyboardShortcut = true,
  onThemeChange,
  onFirstFabClick,
}: Props): JSX.Element {
  const [open, setOpen] = useState(false);
  const [readingMask, setReadingMask] = useState(() => getPreferences().readingMask);
  const [magnifier, setMagnifier] = useState(() => getPreferences().magnifier);
  const baseId = useId();
  const titleId = `${baseId}-title`;
  const descriptionId = `${baseId}-desc`;

  useEffect(() => {
    return on(EVENT_NAMES.CHANGE, (record) => {
      setReadingMask(record.prefs.readingMask);
      setMagnifier(record.prefs.magnifier);
    });
  }, []);

  // Programmatic open via window.BlakfyA11y.open() → emits EVENT_NAMES.OPEN
  useEffect(() => {
    const offOpen = on(EVENT_NAMES.OPEN, () => setOpen(true));
    const offClose = on(EVENT_NAMES.CLOSE, () => setOpen(false));
    return () => {
      offOpen();
      offClose();
    };
  }, []);

  // Alt+0 keyboard shortcut — toggles panel open/closed
  useEffect(() => {
    if (!keyboardShortcut) return;
    if (typeof window === 'undefined') return;
    const handler = (e: KeyboardEvent): void => {
      if (e.altKey && e.key === '0') {
        e.preventDefault();
        setOpen((prev) => {
          if (prev) {
            emit(EVENT_NAMES.CLOSE, {});
            return false;
          }
          emit(EVENT_NAMES.OPEN, {});
          return true;
        });
      }
    };
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, [keyboardShortcut]);

  const handleFabClick = (): void => {
    onFirstFabClick?.();
    setOpen(true);
    emit(EVENT_NAMES.OPEN, {});
  };

  const handleClose = (): void => {
    setOpen(false);
    emit(EVENT_NAMES.CLOSE, {});
  };

  return (
    <>
      <FabButton
        iconStyle={iconStyle}
        ariaLabel={translation.fab.label}
        isOpen={open}
        onClick={handleFabClick}
      />
      <FloatingBadge />
      <ReadingMask enabled={readingMask} />
      <Magnifier enabled={magnifier} />
      <Dialog open={open} onClose={handleClose} titleId={titleId} descriptionId={descriptionId}>
        <Panel
          translation={translation}
          locale={config.locale}
          currentTheme={config.theme}
          onClose={handleClose}
          onThemeChange={onThemeChange ?? (() => undefined)}
          titleId={titleId}
          descriptionId={descriptionId}
        />
      </Dialog>
    </>
  );
}
